import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

import { SousCategorie } from 'app/models/sous-categorie';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-sous-categorie',
  templateUrl: './modifier-sous-categorie.component.html',
  styleUrls: ['./modifier-sous-categorie.component.scss']
})
export class ModifierSousCategorieComponent implements OnInit {
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  sousCategorie: SousCategorie = new SousCategorie();
  idSouCat: string | null = null;

  // Liste pour image_url (stocke uniquement les clés S3, sans le domaine)
  imageUrlList: string[] = [];
  uploadingToImageUrl = false;

  // Liste pour listimages (stocke les URLs complètes ou clés, tel quel)
  listimagesList: string[] = [];
  uploadingToListimages = false;

  isSubmitting = false;

  private originalImageUrlList: string[] = [];
  private originalListimagesList: string[] = [];

  constructor(
    private sousCatService: SousCategorieService,
    private awsService: AwscontrolService,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idSouCat = this.activatedRoute.snapshot.paramMap.get('id');
    this.getSousCategorieDetails();
  }

  getSousCategorieDetails(): void {
    if (!this.idSouCat) return;
    this.sousCatService.getSousCategorie(this.idSouCat).subscribe({
      next: (data: any) => {
        this.sousCategorie = { ...data } as SousCategorie;

        // Charger image_url (split par virgule) – ce sont déjà des clés S3
        if (data.image_url) {
          this.imageUrlList = data.image_url.split(',').map((url: string) => url.trim()).filter((url: string) => url);
        } else {
          this.imageUrlList = [];
        }

        // Charger listimages – peut contenir des URLs complètes ou des clés
        this.listimagesList = (data.listimages && Array.isArray(data.listimages)) ? [...data.listimages] : [];

        this.originalImageUrlList = [...this.imageUrlList];
        this.originalListimagesList = [...this.listimagesList];

        console.log('image_url list (keys):', this.imageUrlList);
        console.log('listimages list:', this.listimagesList);
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de charger les détails', confirmButtonColor: '#f44336' });
      }
    });
  }

  // Retourne l'URL complète pour l'affichage (ajoute le domaine si c'est une clé)
  getFullImageUrl(urlOrKey: string): string {
    if (!urlOrKey) return 'assets/default-subproduct.png';
    if (urlOrKey.startsWith('http')) return urlOrKey;
    return this.s3BaseUrl + urlOrKey;
  }

  // ---------- Upload fichiers pour image_url (stockage clé S3) ----------
  onFileSelectedForImageUrl(event: any): void {
    const files = event.target.files;
    if (files && files.length) {
      this.uploadFilesToS3(files, 'image_url');
    }
  }

  // ---------- Upload fichiers pour listimages (stockage URL complète) ----------
  onFileSelectedForListimages(event: any): void {
    const files = event.target.files;
    if (files && files.length) {
      this.uploadFilesToS3(files, 'listimages');
    }
  }

  private uploadFilesToS3(files: FileList, target: 'image_url' | 'listimages'): void {
    const filesArray = Array.from(files);
    const validFiles: File[] = [];
    const invalidFiles: { file: File; error: string }[] = [];

    filesArray.forEach(file => {
      const validation = this.validateImageFile(file);
      if (validation.valid) validFiles.push(file);
      else invalidFiles.push({ file, error: validation.error || 'Fichier invalide' });
    });

    if (invalidFiles.length) {
      invalidFiles.forEach(inv => {
        Swal.fire({ icon: 'warning', title: 'Fichier ignoré', text: `${inv.file.name}: ${inv.error}`, timer: 2000, showConfirmButton: false });
      });
    }

    if (!validFiles.length) return;

    if (target === 'image_url') this.uploadingToImageUrl = true;
    else this.uploadingToListimages = true;

    this.awsService.uploadFiles(validFiles, 'sous-categories').subscribe({
      next: (response) => {
        if (target === 'image_url') this.uploadingToImageUrl = false;
        else this.uploadingToListimages = false;

        if (response.success && response.urls && response.urls.length) {
          if (target === 'image_url') {
            // Pour image_url : on ne garde que la clé S3 (supprime le domaine)
            const keys = response.urls.map((fullUrl: string) => fullUrl.replace(this.s3BaseUrl, ''));
            this.imageUrlList.push(...keys);
          } else {
            // Pour listimages : on garde l'URL complète
            this.listimagesList.push(...response.urls);
          }
          Swal.fire({ icon: 'success', title: 'Upload réussi', text: `${response.urls.length} image(s) ajoutée(s)`, timer: 1500, showConfirmButton: false });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
        // Réinitialiser l'input file
        const fileInput = target === 'image_url' ? document.getElementById('fileInputImageUrl') : document.getElementById('fileInputListimages');
        if (fileInput) (fileInput as HTMLInputElement).value = '';
      },
      error: (err) => {
        if (target === 'image_url') this.uploadingToImageUrl = false;
        else this.uploadingToListimages = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger vers AWS S3', confirmButtonColor: '#f44336' });
        const fileInput = target === 'image_url' ? document.getElementById('fileInputImageUrl') : document.getElementById('fileInputListimages');
        if (fileInput) (fileInput as HTMLInputElement).value = '';
      }
    });
  }

  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      return { valid: false, error: 'Format non supporté. JPEG, PNG, JPG, GIF, WEBP' };
    }
    if (file.size > maxSize) {
      return { valid: false, error: 'Fichier trop volumineux. Max 5MB' };
    }
    return { valid: true };
  }

  // Suppression d'une image de image_url
  removeImageFromImageUrl(index: number): void {
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.imageUrlList.splice(index, 1);
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  // Suppression d'une image de listimages
  removeImageFromListimages(index: number): void {
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.listimagesList.splice(index, 1);
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  // Construction du payload pour le backend
  private buildPayload(): any {
    // image_url : concaténation des clés S3 (sans domaine)
    const image_url = this.imageUrlList.join(',');
    // listimages : tableau d'URLs (complètes ou clés, tel quel)
    const listimages = [...this.listimagesList];

    return {
      name: this.sousCategorie.name?.trim() || '',
      description: this.sousCategorie.description?.trim() || '',
      website: this.sousCategorie.website?.trim() || '',
      category: this.sousCategorie.category?.trim() || '',
      prix: this.sousCategorie.prix?.trim() || '',
      image_url: image_url,
      listimages: listimages
    };
  }

  // Soumission de la mise à jour
  updateSousCat(): void {
    if (!this.sousCategorie.name?.trim() || !this.sousCategorie.category?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Champs requis', text: 'Nom et catégorie sont obligatoires', confirmButtonColor: '#ff9800' });
      return;
    }

    this.isSubmitting = true;
    const payload = this.buildPayload();
    console.log('Payload envoyé:', payload);

    Swal.fire({ title: 'Mise à jour...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    if (!this.idSouCat) {
      Swal.fire({ icon: 'error', title: 'ID manquant', text: 'Impossible de mettre à jour', confirmButtonColor: '#f44336' });
      this.isSubmitting = false;
      return;
    }

    this.sousCatService.updateSousCategorie(this.idSouCat, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Succès !', text: 'Sous-catégorie mise à jour', timer: 2000, showConfirmButton: false }).then(() => this.navigateBack());
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        Swal.close();
        let errorMessage = 'Erreur lors de la mise à jour';
        if (error.error?.message) errorMessage = error.error.message;
        Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
      }
    });
  }

  navigateBack(): void {
    this.location.back();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.getSousCategorieDetails();
        Swal.fire({ icon: 'success', title: 'Réinitialisé', timer: 1500, showConfirmButton: false });
      }
    });
  }
}